/**
 * Update Banner Plugin
 *
 * Listens for Nuxt's built-in signals that a newer build is available and
 * surfaces a reactive flag consumed by `<VersionUpdateBanner />`.
 *
 * Mechanism:
 * - Nuxt's `experimental.appManifest` polls `/_nuxt/builds/meta/<buildId>.json`
 *   every `experimental.checkOutdatedBuildInterval` ms (2 min in our config).
 * - When the fetched manifest ID differs from the running bundle's ID,
 *   Nuxt emits `app:manifest:update` — we flip the flag here.
 * - As a fallback for stale-manifest edge cases (issue nuxt#32912), we also
 *   listen to `app:chunkError` so the banner shows if the user hits a stale
 *   lazy chunk before the polling had a chance to detect the new build.
 *
 * The actual reload is handled by:
 * - User clicking "Reload" in `<VersionUpdateBanner />` (soft mode), OR
 * - Nuxt's built-in `chunk-reload.client.ts` plugin on next navigation (automatic).
 */
export default defineNuxtPlugin((nuxtApp) => {
  const updateAvailable = useState<boolean>('app-update-available', () => false)

  // Primary signal: new build manifest detected by Nuxt's polling.
  // Hook receives { id: string, timestamp: number } — we only need the fact.
  nuxtApp.hook('app:manifest:update', () => {
    updateAvailable.value = true
  })

  // Fallback: chunk error fired (deployment happened mid-session, user tried
  // navigating to a route whose chunk no longer exists). Nuxt's built-in
  // chunk-reload plugin will reload on next navigation; we flip the flag too
  // so the banner also shows if the user stays on the current page.
  //
  // Not while offline, though. Since #819 the deposit/withdraw modals fetch
  // their chunk on first open, so a dropped connection now produces a chunk
  // error on a path that has nothing to do with a deploy — and the banner
  // would tell the user "new build just shipped", which is simply false. That
  // was observed on a production build: go offline, tap Deposit, and the
  // banner appears alongside the modal's own error card (#845). The modal's
  // card is the honest message there; this banner stays out of it.
  nuxtApp.hook('app:chunkError', () => {
    // No `import.meta.client` guard: this is a .client plugin, so navigator is
    // always there — and the guard would be false under vitest, silently
    // disabling the check in exactly the test written to protect it.
    if (navigator.onLine === false) return
    updateAvailable.value = true
  })
})
